import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { Subject, debounceTime, Subscription } from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChevronDownIconComponent,
  CloseIconComponent,
  UsersIconComponent,
} from '@kuiper/icons';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { LoaderComponent } from '../../../../../ui-components/lib/loader/loader.component';
import { ClickOutSideDirective } from '../../../../../ui-components/directives/clickOutSide.directive';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { RoleTypes } from '../../../../common/constants/role-types.constant';
import { AssignInfo } from '../../../../common/models/assignee.model';
import { SearchMinLength } from '../../../../common/enums/search-min-length';
import { Status } from '../../../../common/enums/user-status';

@Component({
  selector: 'lib-side-menu-assignee-dropdown-view',
  imports: [
    AvatarComponent,
    BadgeComponent,
    LoaderComponent,
    ClickOutSideDirective,
    TranslocoPipe,
    UsersIconComponent,
    CloseIconComponent,
    TrimTooltipDirective,
    ChevronDownIconComponent,
  ],
  templateUrl: './side-menu-assignee-dropdown-view.component.html',
  styleUrl: './side-menu-assignee-dropdown-view.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SideMenuAssigneeDropdownViewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  featureFlagsService = inject(FeatureFlagsService);

  Status = Status;
  RoleTypes = RoleTypes;

  userTableList = input.required<AssignInfo[]>();
  userCount = input.required<number>();
  containerRef = input.required<string>();
  croppedArray = signal<AssignInfo[]>([]);

  userDropdownList = model<any[]>([]);
  isDropdownLoading = input<boolean>(false);
  currentDropdownPage = input<number>(1);
  totalDropdownPages = input<number>(20);
  totalDropdownCount = model<number>(0);
  assigneeType = input<string[]>([]);

  enableLocalSearch = input<boolean>(false);
  searchLabel = input<string>('Find Users');
  searchPlaceholder = input<string>('Search by name'); //to be able to translate or edit it
  noSearchResultLabel = input<string>('No search result'); //to be able to translate or edit it
  headerListLabel = input<string>('Owners'); //to be able to translate or edit it
  displayMode = input<'avatars' | 'underlined-text'>('avatars');
  showCloseButton = input<boolean>(false);

  imgDimensions = input<{ width: number; height: number }>({
    width: 32,
    height: 32,
  });

  isListPositionTop = signal<boolean>(false);
  isMenuOpen = signal(false);
  handleMenuToggle = output<any>();

  searchTerm = signal<string>('');
  searchTerm$ = new Subject<string>();
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);

  subscriptions = new Subscription();

  ngOnInit() {
    if (this.userTableList().length > 3) {
      this.croppedArray.set(this.userTableList().slice(0, 3));
    } else {
      this.croppedArray.set(this.userTableList());
    }
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.searchTerm$.pipe(debounceTime(600)).subscribe(term => {
        this.onDropdownSearch(term);
      })
    );
  }

  toggleMenu(event: any) {
    const containerElement = (
      document.getElementById(this.containerRef()) as HTMLElement
    ).parentElement as HTMLElement;

    const containerCenter =
      (containerElement.getBoundingClientRect().top +
        containerElement.getBoundingClientRect().bottom) /
      2;
    this.isListPositionTop.set(event.clientY > containerCenter);

    this.isMenuOpen.set(!this.isMenuOpen());
    if (this.isMenuOpen()) {
      const param = { currentPage: 1, search: '' };
      this.handleMenuToggle.emit(param);
    }
  }

  onSearchTermChange(target: EventTarget | null) {
    const term = (target as HTMLInputElement).value;
    this.searchTerm$.next(term);
  }

  onDropdownSearch(term: string) {
    if (term.length >= 3) {
      this.isMoreThanThreeCharacters.set(true);
      const param = { currentPage: 1, search: term };
      this.searchTerm.set(term);

      if (!this.enableLocalSearch()) {
        this.handleMenuToggle.emit(param);
      } else {
        const isSearchResult = this.userTableList().filter(user =>
          user.name.toLowerCase().includes(term.toLowerCase())
        );
        this.userDropdownList.set(isSearchResult);
        this.totalDropdownCount.set(this.userDropdownList().length);
      }
    } else if (
      this.isMoreThanThreeCharacters() &&
      term.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      const param = { currentPage: 1, search: '' };
      this.searchTerm.set('');

      if (!this.enableLocalSearch()) {
        this.handleMenuToggle.emit(param);
      } else {
        this.userDropdownList.set(this.userTableList());
        this.totalDropdownCount.set(this.userDropdownList().length);
      }
    }
  }

  onScroll(event: Event) {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as unknown as {
        scrollTop: number;
        scrollHeight: number;
        clientHeight: number;
      };
    if (
      scrollTop + clientHeight >= scrollHeight - 200 &&
      !this.isDropdownLoading() &&
      this.currentDropdownPage() < this.totalDropdownPages()
    ) {
      const param = {
        currentPage: this.currentDropdownPage() + 1,
        search: this.searchTerm(),
      };
      this.handleMenuToggle.emit(param);
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
